import { useEffect, useMemo, useState } from "react";
import { removeCampaignRecipients, type CampaignMessageAssignment, type CampaignRecipientPlan } from "./campaignBuilderApi";
import {
  countByDeliverabilityStatus,
  DELIVERABILITY_STATUSES,
  deliverabilityLabel,
  type DeliverabilityStatus,
} from "./deliverability";
import { CampaignContactLink, type CampaignCrmNavigation } from "./CrmRecordLinks";

export type DeliverabilityFilter = "all" | DeliverabilityStatus;

export function PlannedRecipientsDrawer({
  open,
  campaignId,
  stepId,
  stepName,
  assignments,
  onRecipientPlanChange,
  onClose,
  crmNavigation,
}: {
  open: boolean;
  campaignId: string;
  stepId: string;
  stepName: string;
  assignments: CampaignMessageAssignment[];
  onRecipientPlanChange: (plan: CampaignRecipientPlan) => void;
  onClose: () => void;
  crmNavigation?: CampaignCrmNavigation;
}) {
  const [statusFilter, setStatusFilter] = useState<DeliverabilityFilter>("all");
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStatusFilter("all");
      setRemoveError(null);
    }
  }, [open, stepId]);

  const statusCounts = useMemo(() => countByDeliverabilityStatus(assignments), [assignments]);

  const sorted = useMemo(() => {
    const filtered =
      statusFilter === "all"
        ? assignments
        : assignments.filter((row) => row.deliverability_status === statusFilter);
    return [...filtered].sort((a, b) => {
      const an = [a.last_name, a.first_name].filter(Boolean).join(" ") || a.email;
      const bn = [b.last_name, b.first_name].filter(Boolean).join(" ") || b.email;
      return an.localeCompare(bn);
    });
  }, [assignments, statusFilter]);

  if (!open) return null;

  async function onRemovePlanned(email: string) {
    setRemovingEmail(email);
    setRemoveError(null);
    try {
      const plan = await removeCampaignRecipients(campaignId, [email], [stepId]);
      onRecipientPlanChange(plan);
    } catch (err: unknown) {
      setRemoveError(err instanceof Error ? err.message : "Could not remove recipient");
    } finally {
      setRemovingEmail(null);
    }
  }

  return (
    <div className="campaign-drawer-root">
      <button type="button" className="campaign-drawer-backdrop" aria-label="Close planned recipients" onClick={onClose} />
      <aside className="campaign-drawer" aria-labelledby="planned-recipients-drawer-title">
        <header className="campaign-drawer-head">
          <div>
            <h2 id="planned-recipients-drawer-title">
              <span className="journey-email-icon" aria-hidden="true">✉</span>
              Planned recipients
            </h2>
            <p className="hint">{stepName}</p>
          </div>
          <button type="button" className="ghost campaign-drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p className="campaign-drawer-meta hint">
          <strong>{sorted.length}</strong>
          {statusFilter === "all" ? (
            <>
              {" "}
              contact{assignments.length === 1 ? "" : "s"}
            </>
          ) : (
            <> shown · {assignments.length} total</>
          )}{" "}
          · Campaign → contact → email assignment
        </p>
        <div className="campaign-drawer-status-filters" role="group" aria-label="Filter by deliverability status">
          <button
            type="button"
            className={`deliverability-status-filter${statusFilter === "all" ? " is-active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All <span className="deliverability-status-filter-count">{assignments.length}</span>
          </button>
          {DELIVERABILITY_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              className={`deliverability-status-filter deliverability-status-filter-${status}${statusFilter === status ? " is-active" : ""}${statusCounts[status] === 0 ? " is-empty" : ""}`}
              onClick={() => setStatusFilter(status)}
              disabled={statusCounts[status] === 0}
              title={deliverabilityLabel(status)}
            >
              {deliverabilityLabel(status)}{" "}
              <span className="deliverability-status-filter-count">{statusCounts[status]}</span>
            </button>
          ))}
        </div>
        {removeError ? (
          <p className="campaign-drawer-remove-error hint" role="alert">
            {removeError}
          </p>
        ) : null}
        <div className="campaign-drawer-body">
          {sorted.length ? (
            <table className="rating-table campaign-planned-drawer-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Deliverability</th>
                  <th className="campaign-planned-drawer-remove-col" aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  const isPlanned = row.deliverability_status === "planned";
                  const removing = removingEmail === row.email;
                  const contactLabel =
                    [row.first_name, row.last_name].filter(Boolean).join(" ") || row.email;
                  return (
                    <tr key={row.assignment_id}>
                      <td>
                        <CampaignContactLink
                          contactId={row.contact_id}
                          label={[row.first_name, row.last_name].filter(Boolean).join(" ") || row.email || "—"}
                          navigation={crmNavigation}
                        />
                      </td>
                      <td>{row.email}</td>
                      <td>
                        <span
                          className={`deliverability-badge deliverability-${row.deliverability_status as DeliverabilityStatus}`}
                        >
                          {deliverabilityLabel(row.deliverability_status)}
                        </span>
                      </td>
                      <td className="campaign-planned-drawer-remove-col">
                        {isPlanned ? (
                          <button
                            type="button"
                            className="ghost icon-btn campaign-planned-remove-btn"
                            title={`Remove ${contactLabel} from this email`}
                            aria-label={`Remove ${contactLabel} from this email`}
                            disabled={removing || (removingEmail !== null && !removing)}
                            onClick={() => void onRemovePlanned(row.email)}
                          >
                            ×
                          </button>
                        ) : (
                          <span className="hint" title="Staged or past planned — already triggered, so this contact stays on the email">
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : assignments.length ? (
            <p className="hint">No contacts match this deliverability filter.</p>
          ) : (
            <p className="hint">No contacts assigned to this email yet.</p>
          )}
        </div>
      </aside>
    </div>
  );
}
